// src/App.tsx

import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Button, 
  Box, 
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  Fab,
  Tooltip,
  Autocomplete,
  Slider,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Link,
  Menu
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap, ZoomControl } from 'react-leaflet';
import type { Map as LeafletMap } from 'leaflet';
import { Delete as DeleteIcon, PhotoCamera, Upload, LocationOn, ExpandMore, ExpandLess, MyLocation, Directions, Search, Add, Remove, Report as ReportIcon, AdminPanelSettings } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import appwriteService from "./services/AppwriteService";
import { Models } from 'appwrite';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import AuthCallback from './components/AuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';

interface Postbox {
  $id: string;
  $createdAt: string;
  $updatedAt: string;
  userId: string;
  latitude: number;
  longitude: number;
  royalCipher: string;
  description: string;
  photoUrl: string;
}

interface NewPostbox {
  latitude: number;
  longitude: number;
  royalCipher: string;
  description: string;
  photoUrl: string;
}

interface Report extends Models.Document {
  postboxId: string;
  userId: string;
  reason: string;
  description?: string;
  status: 'pending' | 'reviewed' | 'resolved';
  createdAt: string;
}

const ROYAL_CIPHERS = [
  { value: ' ', label: 'Unknown/Anonymous' },
  { value: 'CRIII', label: 'Charles III' },
  { value: 'ERII', label: 'Elizabeth II' },
  { value: 'GVI', label: 'George VI' },
  { value: 'EVIII', label: 'Edward VIII' },
  { value: 'GV', label: 'George V' },
  { value: 'EVII', label: 'Edward VII' },
  { value: 'VR', label: 'Victoria' }
];

const RADIUS_OPTIONS = [
  { value: 1, label: '1 km' },
  { value: 2, label: '2 km' },
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 20, label: '20 km' }
];

function MapClickHandler({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Create custom marker icon
const createCustomIcon = (cipher: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: #E60012;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">
        ${cipher}
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });
};

// Add back the location marker function
const createLocationIcon = () => {
  return L.divIcon({
    className: 'location-marker',
    html: `
      <div style="
        background-color: #4CAF50;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      "></div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

function AppContent() {
  const [postboxes, setPostboxes] = useState<Postbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([51.5074, -0.1278]); // Default to London
  const [isMapReady, setIsMapReady] = useState(false);
  const [newPostbox, setNewPostbox] = useState<NewPostbox>({
    latitude: 0,
    longitude: 0,
    royalCipher: '',
    description: '',
    photoUrl: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportingPostbox, setReportingPostbox] = useState<Postbox | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [user, setUser] = useState<Models.User<Models.Preferences> | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchRadius, setSearchRadius] = useState<number>(5);
  const [searchResults, setSearchResults] = useState<Postbox[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const mapRef = useRef<LeafletMap | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [isGettingDirections, setIsGettingDirections] = useState(false);
  const [selectedCipher, setSelectedCipher] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [displayedCipher, setDisplayedCipher] = useState<string | null>(null);
  const [displayedRadius, setDisplayedRadius] = useState<number>(5);
  const [isControlsExpanded, setIsControlsExpanded] = useState(true);
  const [reports, setReports] = useState<Report[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Regular auth check
        const user = await appwriteService.getCurrentUser();
        if (user) {
          setUser(user);
          await fetchPostboxes();
        }
      } catch (error) {
        console.error('Auth error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation: [number, number] = [
            position.coords.latitude,
            position.coords.longitude
          ];
          setUserLocation(newLocation);
          setMapCenter(newLocation);
          if (mapRef.current) {
            mapRef.current.flyTo(newLocation, 16, {
              duration: 1.5,
              easeLinearity: 0.25
            });
          }
          setIsMapReady(true);
        },
        (error) => {
          console.error("Error getting location:", error);
          setIsMapReady(true); // Set map as ready even if location fails
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      setIsMapReady(true); // Set map as ready if geolocation is not available
    }
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const currentUser = await appwriteService.getCurrentUser();
      if (currentUser && currentUser.$id === '680ca64779a3b48bd0cc') {
        setIsAdmin(true);
        await fetchReports();
      }
    };
    checkAdminStatus();
  }, []);

  // Add polling for reports
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (isAdmin && selectedTab === 1) {
      // Fetch reports immediately
      fetchReports();
      
      // Then poll every 10 seconds
      intervalId = setInterval(fetchReports, 10000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAdmin, selectedTab]);

  const handleSignIn = async () => {
    if (!privacyAgreed) {
      setError("Please agree to the privacy policy before signing in");
      return;
    }
    try {
      await appwriteService.signInWithGoogle();
    } catch (error) {
      setError("Failed to sign in with Google");
    }
  };

  const handleFacebookSignIn = async () => {
    if (!privacyAgreed) {
      setError("Please agree to the privacy policy before signing in");
      return;
    }
    try {
      await appwriteService.signInWithFacebook();
    } catch (error) {
      setError("Failed to sign in with Facebook");
    }
  };

  const handleSignOut = async () => {
    try {
      await appwriteService.signOut();
      setUser(null);
    } catch (error) {
      setError("Failed to sign out");
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setSelectedLocation({ lat, lng });
    setNewPostbox(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedLocation(null);
    setNewPostbox({
      latitude: 0,
      longitude: 0,
      royalCipher: '',
      description: '',
      photoUrl: ''
    });
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewPostbox(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('File selected:', file);
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraClick = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          console.log('Photo captured:', file);
          setSelectedFile(file);
          const reader = new FileReader();
          reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
      };
      
      input.click();
    } catch (error) {
      console.error('Error accessing camera:', error);
      setError('Failed to access camera. Please ensure you have granted camera permissions.');
    }
  };

  const getDirections = async (latitude: number, longitude: number) => {
    if (isGettingDirections) return;
    setIsGettingDirections(true);

    try {
      if (userLocation) {
        // Use cached location
        const url = `https://www.google.com/maps/dir/?api=1&origin=${userLocation[0]},${userLocation[1]}&destination=${latitude},${longitude}&travelmode=walking`;
        window.open(url, '_blank');
      } else {
        // Get fresh location if not cached
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000
          });
        });
        
        const url = `https://www.google.com/maps/dir/?api=1&origin=${position.coords.latitude},${position.coords.longitude}&destination=${latitude},${longitude}&travelmode=walking`;
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error("Error getting directions:", error);
      // If we can't get current location, just open the destination
      const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(url, '_blank');
    } finally {
      setIsGettingDirections(false);
    }
  };

  const handleSearch = () => {
    if (!selectedCipher) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Get current map center
    const center = mapRef.current?.getCenter();
    if (!center) return;

    // Convert radius from km to meters
    const radiusInMeters = searchRadius * 1000;

    // First filter by exact cipher match
    const cipherResults = postboxes.filter(postbox => 
      postbox.royalCipher.toLowerCase() === selectedCipher.toLowerCase()
    );

    // Then filter by distance
    const results = cipherResults.filter(postbox => {
      const postboxLatLng = L.latLng(postbox.latitude, postbox.longitude);
      const distance = center.distanceTo(postboxLatLng);
      return distance <= radiusInMeters;
    });

    // Sort results by distance
    results.sort((a, b) => {
      const distA = center.distanceTo(L.latLng(a.latitude, a.longitude));
      const distB = center.distanceTo(L.latLng(b.latitude, b.longitude));
      return distA - distB;
    });

    setSearchResults(results);
    setShowSearchResults(true);
    setHasSearched(true);
    setDisplayedCipher(selectedCipher);
    setDisplayedRadius(searchRadius);
  };

  const handleRadiusChange = (event: SelectChangeEvent<number>) => {
    setSearchRadius(event.target.value as number);
    setHasSearched(false);
  };

  const clearSearch = () => {
    setSelectedCipher(null);
    setSearchResults([]);
    setShowSearchResults(false);
    setHasSearched(false);
    setDisplayedCipher(null);
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportingPostbox || !reportReason) return;

    try {
      setLoading(true);
      await appwriteService.createReport({
        postboxId: reportingPostbox.$id,
        reason: reportReason,
        description: reportDescription
      });
      setIsReportDialogOpen(false);
      setReportingPostbox(null);
      setReportReason('');
      setReportDescription('');
      setError("Thank you for your report. Our team will review it shortly.");
    } catch (error) {
      console.error("Error submitting report:", error);
      setError("Failed to submit report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReportClick = (postbox: Postbox) => {
    setReportingPostbox(postbox);
    setIsReportDialogOpen(true);
  };

  const MapClickHandler = ({ onClick }: { onClick: (lat: number, lng: number) => void }) => {
    const map = useMapEvents({
      click: (e) => {
        // Only trigger if we're not in a dialog
        if (!isDialogOpen && !isReportDialogOpen) {
          onClick(e.latlng.lat, e.latlng.lng);
        }
      },
    });
    return null;
  };

  async function addPostbox(e?: React.FormEvent) {
    if (e) {
      e.preventDefault();
    }

    try {
      setLoading(true);
      let photoUrl = '';
      
      if (selectedFile) {
        try {
          console.log('Uploading photo:', selectedFile);
          photoUrl = await appwriteService.uploadPhoto(selectedFile);
          console.log('Photo uploaded successfully. URL:', photoUrl);
        } catch (uploadError) {
          console.error('Error uploading photo:', uploadError);
          setError("Failed to upload photo. Please try again.");
          setLoading(false);
          return;
        }
      }

      const postboxData = {
        ...newPostbox,
        photoUrl: photoUrl || ''
      };

      console.log('Creating postbox with data:', postboxData);
      const result = await appwriteService.createPostbox(postboxData);
      console.log('Postbox created successfully:', result);
      
      // Keep the map centered on the newly added postbox
      if (mapRef.current) {
        const newPostboxLocation: [number, number] = [newPostbox.latitude, newPostbox.longitude];
        setMapCenter(newPostboxLocation);
        mapRef.current.flyTo(newPostboxLocation, 16, {
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
      
      await fetchPostboxes();
      handleDialogClose();
    } catch (error) {
      console.error("Error adding postbox:", error);
      setError("Failed to add postbox. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function deletePostbox(id: string) {
    try {
      setLoading(true);
      await appwriteService.deletePostbox(id);
      await fetchPostboxes();
    } catch (error) {
      setError("Failed to delete postbox");
      console.error("Error deleting postbox:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchPostboxes() {
    try {
      setLoading(true);
      const response = await appwriteService.listPostboxes();
      const postboxes = response.documents.map(doc => ({
        $id: doc.$id,
        $createdAt: doc.$createdAt,
        $updatedAt: doc.$updatedAt,
        userId: doc.userId,
        latitude: doc.latitude / 1000000,
        longitude: doc.longitude / 1000000,
        royalCipher: doc.royalCipher,
        description: doc.description,
        photoUrl: doc.photoUrl
      }));
      setPostboxes(postboxes);
    } catch (error) {
      setError("Failed to fetch postboxes");
      console.error("Error fetching postboxes:", error);
    } finally {
      setLoading(false);
    }
  }

  const LocationMarker = () => {
    if (!userLocation) return null;
    return (
      <Marker
        position={userLocation}
        icon={createLocationIcon()}
      />
    );
  };

  const handleLocationClick = () => {
    if (!userLocation) {
      // If we don't have a location yet, try to get it
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation: [number, number] = [
            position.coords.latitude,
            position.coords.longitude
          ];
          setUserLocation(newLocation);
          if (mapRef.current) {
            mapRef.current.flyTo(newLocation, 16, {
              duration: 1.5,
              easeLinearity: 0.25
            });
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          // Show error to user
          setError("Could not get your location. Please ensure location services are enabled.");
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else if (mapRef.current) {
      // If we already have the location, just center the map
      mapRef.current.flyTo(userLocation, 16, {
        duration: 1.5,
        easeLinearity: 0.25
      });
    }
  };

  const SearchControls = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Autocomplete
          options={ROYAL_CIPHERS.map(cipher => cipher.value)}
          value={selectedCipher}
          onChange={(event, value) => {
            setSelectedCipher(value);
            setHasSearched(false);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Royal Cipher"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <Search sx={{ mr: 1 }} />
              }}
            />
          )}
          sx={{ minWidth: 200, flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Search Radius</InputLabel>
          <Select
            value={searchRadius}
            label="Search Radius"
            onChange={handleRadiusChange}
          >
            {RADIUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Button
        variant="contained"
        onClick={handleSearch}
        startIcon={<Search />}
        fullWidth
        disabled={!selectedCipher || hasSearched}
        color={hasSearched ? "inherit" : "primary"}
      >
        Search
      </Button>
    </Box>
  );

  const SearchResults = () => {
    if (!showSearchResults) return null;

    const center = mapRef.current?.getCenter();
    if (!center) return null;

    const cipherLabel = ROYAL_CIPHERS.find(c => c.value === displayedCipher)?.label || displayedCipher;

    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 1,
          maxHeight: 200,
          overflow: 'auto',
          backgroundColor: 'rgba(255, 255, 255, 0.7)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            Found {searchResults.length} {cipherLabel} postbox{searchResults.length !== 1 ? 'es' : ''} within {displayedRadius}km
          </Typography>
          <IconButton 
            size="small" 
            onClick={clearSearch}
            title="Clear search"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        <Stack spacing={1}>
          {searchResults.map(postbox => {
            const distance = center.distanceTo(L.latLng(postbox.latitude, postbox.longitude)) / 1000;
            
            return (
              <Chip
                key={postbox.$id}
                label={`${postbox.royalCipher} (${distance.toFixed(1)}km)`}
                onClick={() => {
                  if (mapRef.current) {
                    mapRef.current.flyTo([postbox.latitude, postbox.longitude], 16, {
                      duration: 1.5,
                      easeLinearity: 0.25
                    });
                  }
                }}
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const ControlsHeader = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
      onClick={() => setIsControlsExpanded(!isControlsExpanded)}
    >
      <Typography variant="h5" component="h1">
        Postbox Explorer
      </Typography>
      <IconButton size="small">
        {isControlsExpanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    </Box>
  );

  const ControlsContent = () => {
    // Type assertion since we know user is not null in this context
    const currentUser = user as Models.User<Models.Preferences>;
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    
    const handleDeleteAccount = async () => {
      try {
        setIsDeleting(true);
        await appwriteService.deleteAccount();
        setUser(null);
        navigate('/');
      } catch (error) {
        console.error('Error deleting account:', error);
        setError('Failed to delete account. Please try again.');
      } finally {
        setIsDeleting(false);
        setIsDeleteDialogOpen(false);
        handleMenuClose();
      }
    };
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={handleMenuOpen}
          >
            <Avatar 
              src={currentUser.prefs?.avatar} 
              alt={currentUser.name || 'User'}
              sx={{ width: 32, height: 32 }}
            />
            <Typography variant="body1">{currentUser.name || 'User'}</Typography>
          </Box>
          <Button 
            variant="outlined" 
            onClick={handleSignOut}
            size="small"
          >
            Sign Out
          </Button>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 200
            }
          }}
        >
          <MenuItem 
            onClick={() => {
              setIsDeleteDialogOpen(true);
              handleMenuClose();
            }}
            sx={{ 
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.contrastText'
              }
            }}
          >
            Delete Account
          </MenuItem>
        </Menu>

        <SearchControls />
        <SearchResults />

        {/* Delete Account Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => !isDeleting && setIsDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Account</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete your account? This action cannot be undone and will permanently delete:
            </Typography>
            <ul>
              <li>Your account information</li>
              <li>All postboxes you've added</li>
              <li>All reports you've submitted</li>
            </ul>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              color="error"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={20} /> : null}
            >
              {isDeleting ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          position: 'relative',
          zIndex: 1000
        }}>
          <Tooltip title="My Location">
            <IconButton
              onClick={handleLocationClick}
              sx={{ 
                backgroundColor: 'white',
                boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <MyLocation />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom In">
            <IconButton
              onClick={() => mapRef.current?.zoomIn()}
              sx={{ 
                backgroundColor: 'white',
                boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <Add />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton
              onClick={() => mapRef.current?.zoomOut()}
              sx={{ 
                backgroundColor: 'white',
                boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <Remove />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Admin Panel">
              <Button
                variant="contained"
                onClick={() => setSelectedTab(1)}
                startIcon={<AdminPanelSettings />}
                sx={{ 
                  backgroundColor: 'white',
                  color: 'black',
                  boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Admin
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  // Optimize postbox rendering
  const PostboxMarkers = React.memo(() => (
    <MarkerClusterGroup>
      {postboxes.map((postbox) => (
        <Marker 
          key={postbox.$id} 
          position={[postbox.latitude, postbox.longitude]}
          icon={createCustomIcon(postbox.royalCipher)}
        >
          <Popup 
            closeButton={false}
            className="custom-popup"
            maxWidth={300}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                {postbox.royalCipher}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {postbox.description}
              </Typography>
              
              {postbox.photoUrl && (
                <Box sx={{ mb: 2 }}>
                  <img 
                    src={postbox.photoUrl} 
                    alt={postbox.description}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 4
                    }}
                  />
                </Box>
              )}
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                gap: 1
              }}>
                <Chip 
                  icon={<LocationOn />}
                  label={`${postbox.latitude.toFixed(6)}, ${postbox.longitude.toFixed(6)}`}
                  size="small"
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Get Directions">
                    <IconButton 
                      onClick={() => getDirections(postbox.latitude, postbox.longitude)}
                      size="small"
                      sx={{ 
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }}
                    >
                      <Directions />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Report Postbox">
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent map click
                        handleReportClick(postbox);
                      }}
                      size="small"
                      sx={{ 
                        backgroundColor: 'warning.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'warning.dark'
                        }
                      }}
                    >
                      <ReportIcon />
                    </IconButton>
                  </Tooltip>
                  {(user && (postbox.userId === user.$id || isAdmin)) && (
                    <Tooltip title="Delete Postbox">
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent map click
                          deletePostbox(postbox.$id);
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          </Popup>
        </Marker>
      ))}
    </MarkerClusterGroup>
  ));

  const fetchReports = async () => {
    try {
      setIsAdminLoading(true);
      const response = await appwriteService.listReports();
      setReports(response.documents);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError("Failed to fetch reports");
    } finally {
      setIsAdminLoading(false);
    }
  };

  const handleReportStatusChange = async (reportId: string, newStatus: 'pending' | 'reviewed' | 'resolved') => {
    try {
      await appwriteService.updateReportStatus(reportId, newStatus);
      await fetchReports();
    } catch (error) {
      console.error("Error updating report status:", error);
      setError("Failed to update report status");
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      await appwriteService.deleteReport(reportId);
      await fetchReports();
    } catch (error) {
      console.error("Error deleting report:", error);
      setError("Failed to delete report");
    }
  };

  const AdminDashboard = () => {
    if (!isAdmin) return null;

    return (
      <Dialog
        open={selectedTab === 1}
        onClose={() => setSelectedTab(0)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
            position: 'relative'
          }
        }}
      >
        <DialogTitle>Admin Dashboard - Reports</DialogTitle>
        <DialogContent>
          {isAdminLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Postbox ID</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.$id}>
                      <TableCell>
                        <Button
                          onClick={() => {
                            const postbox = postboxes.find(p => p.$id === report.postboxId);
                            if (postbox && mapRef.current) {
                              const location: [number, number] = [postbox.latitude, postbox.longitude];
                              setMapCenter(location);
                              mapRef.current.flyTo(location, 16, {
                                duration: 1.5,
                                easeLinearity: 0.25
                              });
                              setSelectedTab(0); // Close the admin dashboard
                            }
                          }}
                          sx={{ 
                            textTransform: 'none',
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          {report.postboxId}
                        </Button>
                      </TableCell>
                      <TableCell>{report.reason}</TableCell>
                      <TableCell>{report.description || 'No description'}</TableCell>
                      <TableCell>
                        <Select
                          value={report.status}
                          onChange={(e) => handleReportStatusChange(report.$id, e.target.value as 'pending' | 'reviewed' | 'resolved')}
                          size="small"
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="reviewed">Reviewed</MenuItem>
                          <MenuItem value="resolved">Resolved</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>{new Date(report.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteReport(report.$id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTab(0)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
        <MapContainer 
          center={mapCenter}
          zoom={16}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          zoomControl={false}
          whenReady={() => setIsMapReady(true)}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {user && <MapClickHandler onClick={handleMapClick} />}
          {isMapReady && <PostboxMarkers />}
          <LocationMarker />
          <ZoomControl position="bottomright" />
        </MapContainer>
      </Box>

      {!user && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          width: 400,
          maxWidth: '90vw'
        }}>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Welcome to Postbox Explorer
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Sign in with Google or Facebook to start exploring and adding postboxes
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyAgreed}
                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleSignIn}
                size="large"
                disabled={!privacyAgreed}
                startIcon={<GoogleIcon />}
              >
                Sign in with Google
              </Button>
              <Button
                variant="contained"
                onClick={handleFacebookSignIn}
                size="large"
                disabled={!privacyAgreed}
                startIcon={<FacebookIcon />}
                sx={{ backgroundColor: '#1877F2', '&:hover': { backgroundColor: '#166FE5' } }}
              >
                Sign in with Facebook
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {user && (
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'absolute', 
            top: 16, 
            left: '50%', 
            transform: 'translateX(-50%)',
            padding: 2,
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 300,
            maxWidth: '80%',
            width: 'auto',
            transition: 'all 0.3s ease'
          }}
        >
          <ControlsHeader />
          <Collapse in={isControlsExpanded}>
            <ControlsContent />
          </Collapse>
        </Paper>
      )}

      {error && (
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'fixed', 
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: 2,
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '90%',
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%'
          }}>
            <Typography 
              color={error.startsWith('Thank you') ? 'success.main' : 'error'}
              sx={{ 
                flex: 1,
                textAlign: 'center',
                pr: 2
              }}
            >
              {error}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setError(null)}
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}

      <AdminDashboard />

      <Dialog 
        open={isDialogOpen} 
        onClose={handleDialogClose}
        sx={{
          '& .MuiDialog-paper': {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxWidth: 400,
            maxHeight: 'calc(100% - 32px)',
            position: 'relative'
          }
        }}
      >
        <form onSubmit={addPostbox}>
          <DialogTitle>Add New Postbox</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Royal Cipher</InputLabel>
                <Select
                  value={newPostbox.royalCipher}
                  label="Royal Cipher"
                  onChange={(e) => setNewPostbox(prev => ({ ...prev, royalCipher: e.target.value }))}
                  required
                >
                  {ROYAL_CIPHERS.map((cipher) => (
                    <MenuItem key={cipher.value} value={cipher.value}>
                      {cipher.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Description"
                name="description"
                value={newPostbox.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                required
              />

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                >
                  Upload Photo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={handleCameraClick}
                >
                  Take Photo
                </Button>
              </Stack>

              {previewUrl && (
                <Box sx={{ width: '100%', height: 200, position: 'relative' }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 4
                    }}
                  />
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                Location: {selectedLocation?.lat.toFixed(6)}, {selectedLocation?.lng.toFixed(6)}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={!newPostbox.royalCipher || !newPostbox.description || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Add Postbox'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog 
        open={isReportDialogOpen} 
        onClose={() => setIsReportDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxWidth: 400,
            maxHeight: 'calc(100% - 32px)',
            position: 'relative'
          }
        }}
      >
        <form onSubmit={handleReportSubmit}>
          <DialogTitle>Report Postbox</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Reporting postbox: {reportingPostbox?.royalCipher}
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Reason for Report</InputLabel>
                <Select
                  value={reportReason}
                  label="Reason for Report"
                  onChange={(e) => setReportReason(e.target.value)}
                  required
                >
                  <MenuItem value="offensive">Offensive Content</MenuItem>
                  <MenuItem value="incorrect">Incorrect Information</MenuItem>
                  <MenuItem value="duplicate">Duplicate Postbox</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Additional Details"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Please provide more details about your report..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsReportDialogOpen(false)}>Cancel</Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={!reportReason || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Report'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="*" element={<AppContent />} />
    </Routes>
  );
}

export default App;
